import React, {useEffect, useState} from 'react';
import includes from 'lodash/includes';
import orderBy from 'lodash/orderBy';
import TableRow from './TableRow';
import {ColumnType, ExtraHeadingColumn, ITableProps, SortDirection} from './types';
import Icon from '../Icon';
import {useTranslation} from 'react-i18next';

const map = new WeakMap()
let index = 0
const getUniqueKey = (obj: object) => {
    let key = map.get(obj)
    if (!key) {
        key = 'row-key-' + index++
        map.set(obj, key)
    }
    return key
}

export const isMobileColumn = (columnKey: string, mobileColumns: string[]) => {
    return includes(mobileColumns, columnKey);
}
export const isFirstMobileColumn = (
    columnKey: string,
    mobileColumns: string[]
) => {
    return (columnKey === mobileColumns[0]);
}
export const isLastMobileColumn = (
    columnKey: string,
    mobileColumns: string[]
) => {
    return (columnKey === mobileColumns[mobileColumns.length - 1]);
}

const getSortedData = (data: any, sortKey: string, direction: string) => {
    const sorter = (item: any) => {
        const sortItem = item[sortKey];
        if (typeof sortItem === 'number') {
            return sortItem;
        }
        if (sortItem && sortItem.toLowerCase) {
            // Make it lower case when sorting
            return sortItem.toLowerCase();
        }
        return sortItem || item;
    }
    return orderBy(data, [sorter], direction as SortDirection);
}

const Table = (props: ITableProps) => {
    const { t } = useTranslation();
    const [sortedData, setSortedData] = useState(
        getSortedData(props.data, props.defaultSortKey, props.defaultDirection)
    );
    const [direction, setDirection] = useState(props.defaultDirection);
    const [sortKey, setSortKey] = useState(props.defaultSortKey || '');
    const isExpandable = (props.columns && 2 < props.columns.length);
    const breakpointMobileClass = props.breakpointMobile ?? 'md';

    useEffect(() => {
        // If data is updated from outside the table component, we update it.
        setSortedData(getSortedData(props.data, sortKey, direction))
    }, [props.data]);

    const onSort = (newSortKey: string) => {
        const sortDirection = direction === 'desc' ? 'asc' : 'desc';
        const newSortedData = getSortedData(
            sortedData,
            newSortKey,
            sortDirection
        );
        setSortedData(newSortedData);
        setSortKey(newSortKey);
        setDirection(sortDirection);
    }

    const renderHeader = (header: ColumnType) => {
        const isMobileCol = isMobileColumn(header.key, props.columnsMobile);
        const isFirstMobileCol = isFirstMobileColumn(
            header.key,
            props.columnsMobile
        );
        const isLastMobileCol = isLastMobileColumn(
            header.key,
            props.columnsMobile
        );

        const firstMobileColClass = isFirstMobileCol ? 'first-column' : '';
        const lastMobileColClass = isLastMobileCol ? 'last-column' : '';
        const visibleOnMobileClass = isMobileCol ? 'visible-on-mobile' : '';
        const expandableClass = isExpandable ? 'expandable' : '';
        const renderKey = `header_${header.key}`;
        if (header.sortable) {
            const sortedClass = sortKey === header.key ? 'sorted' : '';
            const sortIconName = direction === 'asc' ? 'arrow-down' : 'arrow-up';
            return (
                <th
                    key={renderKey}
                    scope="col"
                    onClick={() => onSort(header.key)}
                    className={`sortable ${sortedClass} ${header.key} ${header.className} ${visibleOnMobileClass} ${firstMobileColClass} ${lastMobileColClass} ${expandableClass} ${breakpointMobileClass}`}
                >
                    <span className="header-text">
                        {header.text}
                        <Icon iconName={sortIconName} inline={true} />
                    </span>
                </th>
            );
        }
        return (
            <th
                key={renderKey}
                scope="col"
                className={`${header.key} ${header.className} ${visibleOnMobileClass} ${firstMobileColClass} ${lastMobileColClass} ${expandableClass} ${breakpointMobileClass}`}
            >
                {header.text}
            </th>
        );
    }
    
    const renderExtraHeadingColumns = (column: ExtraHeadingColumn, index: number) => {
        if (column === null)
        {
            return <th key={`extra-heading-column-${index}`} scope="col" className={`${breakpointMobileClass}`}></th>;
        }
        const isMobileCol = column.visibleInMobile;
        const visibleOnMobileClass = isMobileCol ? 'visible-on-mobile' : '';
        return (
            <th
                key={`extra-heading-column-${index}`}
                scope="col"
                colSpan={column.colspan ?? 1}
                className={`${column.className} ${visibleOnMobileClass} ${breakpointMobileClass}`}
            >
                {column.text}
            </th>
        )
    }

    const renderRow = (item: {[key: string]: any;}, index: number) => {
        let rowId = getUniqueKey(item);
        if (props.uniqueKey && item[props.uniqueKey]) {
            rowId = item[props.uniqueKey];
        }
        const isLastRow = !props.footer && index === sortedData.length - 1;
        return (
            <TableRow
                key={rowId}
                rowId={rowId}
                rowData={item}
                columns={props.columns}
                columnsMobile={props.columnsMobile}
                className={`${isLastRow ? 'last-row' : ''} ${
                    props.skinnyRows ? 'skinny' : ''
                }`}
                renderForServer={props.renderForServer || false}
                breakpointMobile={props.breakpointMobile}
                extraHeadingRow={props.extraHeadingRow}
            />
        );
    }

    return (
        <div className='table-wrapper'>
            <table>
                <caption className={props.hideCaption ? 'sr-only' : ''}>
                    {props.caption}
                </caption>
                <thead className={`table-wrapper__thead ${breakpointMobileClass} ${props.extraHeadingRow ? 'table-wrapper__thead--extra-row' : ''} ${props.stickyHeader ? 'table-wrapper__thead--sticky' : ''}`}>
                {
                    props.extraHeadingRow && (
                        <tr className={`table-row table-row__extra-heading ${props.skinnyRows ? 'skinny' : ''}`}>
                            {props.extraHeadingRow.map((column, index) => renderExtraHeadingColumns(column, index))}
                        </tr>
                    )
                }
                <tr className={`table-row ${props.skinnyRows ? 'skinny' : ''}`}>
                    {props.columns.map(column => renderHeader(column))}
                </tr>
                </thead>
                <tbody className={breakpointMobileClass}>
                    {sortedData.map((rowData, index) =>
                        renderRow(rowData, index)
                    )}
                    {(!sortedData || sortedData.length < 1) && (
                        <tr
                            className={`table-row ${!props.footer ? 'last-row' : ''} ${props.skinnyRows ? 'skinny' : ''} table-nodata-row`}>
                            <td colSpan={props.columns.length}>
                                {props.noDatamessage ||
                                    t('table.nodata.default')}
                            </td>
                        </tr>
                    )}
                    {props.footer && (
                        <tr className="table-footer-row">
                            <td colSpan={props.columns.length}>
                                {props.footer}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default Table;